import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import { money } from "@/lib/api";
import { Hammer, Package, Clock, TrendingUp } from "lucide-react";

export default function CostBreakdown({ estimate }) {
  if (!estimate) return null;
  const { summary, scopes = [], notes = [] } = estimate;
  const sym = summary.currency_symbol || "$";
  const unitLabel = summary.unit_label || "Sq Ft";

  const CTip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="border border-border bg-popover p-3 font-mono2 text-xs shadow-xl">
        <p className="mb-1 font-semibold text-foreground">{label}</p>
        {payload.map((p) => (
          <p key={p.name} style={{ color: p.fill }}>
            {p.name}: {money(p.value, sym)}
          </p>
        ))}
      </div>
    );
  };

  const barData = scopes.map((s) => ({
    name: s.name.length > 16 ? s.name.slice(0, 15) + "…" : s.name,
    Materials: s.material_cost,
    Labor: s.labor_cost,
  }));

  const pieData = [
    { name: "Materials", value: summary.material_cost, fill: "#3B82F6" },
    { name: "Labor", value: summary.labor_cost, fill: "#FDE047" },
  ];

  const stats = [
    { label: "Total Cost", value: money(summary.total_cost, sym), icon: TrendingUp, accent: "text-primary" },
    { label: "Materials", value: money(summary.material_cost, sym), icon: Package, accent: "text-accent" },
    { label: "Labor", value: money(summary.labor_cost, sym), icon: Hammer, accent: "text-primary" },
    { label: `Cost / ${unitLabel}`, value: money(summary.cost_per_unit ?? summary.cost_per_sqft, sym), icon: TrendingUp, accent: "text-foreground" },
    { label: "Timeline", value: `${summary.timeline_weeks} wks`, icon: Clock, accent: "text-foreground" },
  ];

  return (
    <div className="space-y-6" data-testid="cost-breakdown">
      <div className="grid grid-cols-2 border border-border md:grid-cols-5">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="border-b border-r border-border p-4 last:border-r-0 md:border-b-0"
          >
            <div className="mb-2 flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">
              <s.icon className="h-3.5 w-3.5" /> {s.label}
            </div>
            <div className={`font-mono2 text-xl font-semibold ${s.accent}`}>{s.value}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="border border-border p-5 lg:col-span-2">
          <h3 className="mb-4 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
            Material vs Labor by Scope
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData} margin={{ top: 4, right: 4, left: 4, bottom: 60 }}>
              <XAxis dataKey="name" angle={-40} textAnchor="end" interval={0} tick={{ fill: "#94A3B8", fontSize: 10, fontFamily: "IBM Plex Mono" }} />
              <YAxis tickFormatter={(v) => `${sym}${(v / 1000).toFixed(0)}k`} tick={{ fill: "#94A3B8", fontSize: 10, fontFamily: "IBM Plex Mono" }} />
              <Tooltip content={<CTip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="Materials" stackId="a" fill="#3B82F6" />
              <Bar dataKey="Labor" stackId="a" fill="#FDE047" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border p-5">
          <h3 className="mb-4 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Split</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {pieData.map((e) => (
                  <Cell key={e.name} fill={e.fill} stroke="#030712" />
                ))}
              </Pie>
              <Tooltip content={<CTip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 space-y-1.5">
            {pieData.map((p) => (
              <div key={p.name} className="flex items-center justify-between font-mono2 text-xs">
                <span className="flex items-center gap-2 text-muted-foreground">
                  <span className="h-2.5 w-2.5" style={{ background: p.fill }} /> {p.name}
                </span>
                <span className="text-foreground">{money(p.value, sym)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-border">
        <div className="border-b border-border bg-secondary/30 px-5 py-3 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">
          Full Scope of Work
        </div>
        <div className="divide-y divide-border">
          {scopes.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              className="grid grid-cols-12 gap-3 px-5 py-4 transition-colors hover:bg-secondary/20"
              data-testid={`scope-row-${i}`}
            >
              <div className="col-span-12 md:col-span-6">
                <p className="font-semibold text-foreground">{s.name}</p>
                <p className="text-sm text-muted-foreground">{s.description}</p>
              </div>
              <div className="col-span-4 md:col-span-2 text-right font-mono2 text-sm">
                <span className="block text-[10px] uppercase text-muted-foreground">Materials</span>
                <span className="text-accent">{money(s.material_cost, sym)}</span>
              </div>
              <div className="col-span-4 md:col-span-2 text-right font-mono2 text-sm">
                <span className="block text-[10px] uppercase text-muted-foreground">Labor</span>
                <span className="text-primary">{money(s.labor_cost, sym)}</span>
              </div>
              <div className="col-span-4 md:col-span-2 text-right font-mono2 text-sm">
                <span className="block text-[10px] uppercase text-muted-foreground">Total</span>
                <span className="font-semibold text-foreground">{money(s.total, sym)}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {notes.length > 0 && (
        <div className="border border-border border-l-2 border-l-primary bg-secondary/20 p-4">
          <p className="mb-1 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Notes</p>
          <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
            {notes.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
